'use client';

import { useState } from 'react';
import { Banner } from '@fountain/ripple/banner';
import { Button } from '@fountain/ripple/button';
import { Card } from '@fountain/ripple/card';
import { ArrowRightOutlineIcon } from '@fountain/ripple/icon';
import { Text } from '@fountain/ripple/text';
import { TextField } from '@fountain/ripple/text-field';
import { Icons } from '@/components/icons';
import { postSignInTarget } from '@/lib/auth/safe-redirect';

interface LoginLookupResponse {
  url?: string;
  unified_auth: boolean;
  error?: string;
}

/**
 * Sign-in page - Email entry
 *
 * Flow:
 * 1. User enters email
 * 2. Call login-lookup API for the account's WX domain
 * 3a. Found → redirect to that WX login page
 * 3b. Not found → show an error; WX is the only sign-in method
 */
export default function SignInPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Please enter your email address');
      setIsLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      // Resolve the WX domain that issues this account's session.
      const response = await fetch('/api/v1/login-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      if (!response.ok) {
        throw new Error('Failed to check login configuration');
      }

      const data: LoginLookupResponse = await response.json();

      if (data.unified_auth && data.url) {
        // UNIFIED AUTH FLOW: Redirect to WX login
        const wxLoginUrl = new URL(data.url);
        const appBaseUrl =
          process.env.NEXT_PUBLIC_APP_DOMAIN ?? window.location.origin;
        const callback = new URL('/api/wx/unified-callback', appBaseUrl);
        const target = postSignInTarget(
          new URLSearchParams(window.location.search).get('return_to')
        );
        if (target) {
          callback.searchParams.set('return_to', target);
        }

        /* Important:
         * Keep the order of search param set calls, so that
         * email is NOT set as the last search param.
         * In WX, there's an extra "Append ?showSignupLink query param" logic in the login page,
         * (not at the app level, but I suspect it's a CF level redirect),
         * which end up adding '?showSignupLink' after the email -> Form reading it as part of the email.
         */
        wxLoginUrl.searchParams.set('email', trimmedEmail);
        wxLoginUrl.searchParams.set('layout', 'platform');
        // WX will POST back to this callback
        wxLoginUrl.searchParams.set('returnTo', callback.toString());

        // Redirect to WX
        window.location.href = wxLoginUrl.toString();
      } else {
        // WX issues every session, so an email that resolves to no WX domain
        // has no second sign-in method to fall back to.
        setError(
          "We couldn't find an account for that email. Check the address, or contact your admin for access."
        );
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Login lookup error:', err);
      setError('Unable to verify your account. Please try again.');
      setIsLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-slate-100 to-indigo-50">
      <div className="w-full max-w-md px-4">
        <Card size="lg">
          {/* Card adds no gap between its children. */}
          <div className="flex flex-col gap-6">
            <Icons.fountainLogo className="h-5 w-auto fill-[#1260FD]" />
            <div className="flex flex-col gap-2 text-center">
              <h1>
                <Text variant="h4" render={<span />}>
                  Let&apos;s get you signed in
                </Text>
              </h1>
              <Text variant="subtitle1">
                We&apos;ll direct you to the right sign-in method for your
                account
              </Text>
            </div>
            <form
              onSubmit={handleSubmit}
              className="flex w-full flex-col gap-6"
            >
              <div className="flex flex-col gap-2">
                <Text variant="body2" render={<label htmlFor="email" />}>
                  Email
                </Text>
                <TextField
                  id="email"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  // A disabled Ripple field fades the address; read-only keeps
                  // it legible while the lookup runs.
                  readOnly={isLoading}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              {error && <Banner variant="error">{error}</Banner>}

              <Button
                className="w-full"
                type="submit"
                endIcon={ArrowRightOutlineIcon}
                loading={isLoading}
                // `loading` stops only the pointer. Disabling the button also
                // stops Enter in the field from sending a second lookup, and
                // `focusableWhenDisabled` keeps focus and the loading style
                // instead of Ripple's faded disabled one.
                disabled={isLoading}
                focusableWhenDisabled
              >
                Continue
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </main>
  );
}
